import { SalesforceResourceMappingLocalData } from 'module/api/integrations/salesforce/types';
import { IconShell } from 'module/ui/components/atoms/icon-shell';
import { OutsideClickHandler } from 'module/ui/components/atoms/outside-click-handler';
import {
  Control,
  Select,
  CustomOption as CustomOptionsType,
  ISingleValue as SingleValue,
  DropdownIndicator,
} from 'module/ui/components/atoms/select';
import { Menu } from 'module/ui/components/atoms/select/components';
import { VirtualizedMenuList } from 'module/ui/components/molecules/grouped-dropdown/virtualised-list';
import { faSalesforce } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@rocketlane/ui';
import { Popover, Space, Typography } from 'antd';
import { groupBy, initial } from 'lodash';
import { useMemo, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { components as RSComponents } from 'react-select';
import styled from 'styled-components';
import {
  getFieldIcon,
  getUiFieldType,
} from 'components/organisms/fields/field-types';
import { RocketlaneFieldValue } from './rocketlane-field-value';
import { SalesforceFieldValue } from './salesforce-field-value';
import './index.css';

// Function to check if click is on a related element (popover content)
const isRelatedEl = (el: HTMLElement) => {
  // Allow clicks inside the popover content, select dropdown, or react-select menus
  return (
    el.closest('.popover') !== null ||
    el.closest('.ant-select-dropdown') !== null ||
    el.closest('.ant-popover') !== null ||
    el.closest('[class*="ozone-select"]') !== null ||
    el.closest('[class*="menu"]') !== null
  );
};

type DefaultProps = {
  triggerLabel?: React.JSX.Element | string;
  value?: SingleValue;
  type: 'salesforce' | 'rocketlane';
  loading?: boolean;
};

type PopoverSelectProps = DefaultProps & {
  onVisibilityChange?: (value: boolean) => void;
  options?: CustomOptionsType[];
  onChange?: (changed?: SingleValue) => void;
  fieldObject: SalesforceResourceMappingLocalData;
  zIndex?: number;
  parentVisible?: boolean;
};

type TriggerRendererProps = DefaultProps & {
  onTriggerClick: () => void;
};

const SelectContainer = styled.div`
  height: 350px;
  width: 272px;
`;

const CustomButton = styled(Button)`
  padding: auto 0px;
`;

const TriggerRenderer = ({
  triggerLabel,
  onTriggerClick,
  value,
  type = 'rocketlane',
  loading,
}: TriggerRendererProps) => {
  if (value) {
    switch (type) {
      case 'salesforce': {
        return (
          <Button
            variant="link"
            onClick={onTriggerClick}
            loading={loading}
            data-cy={`integrations.salesforce_field_value_add_${value.label}`}
          >
            <SalesforceFieldValue label={value.label} />
          </Button>
        );
      }

      case 'rocketlane': {
        return (
          <Button
            variant="link"
            onClick={onTriggerClick}
            loading={loading}
            data-cy="integrations.rocketlane_field_value_add"
          >
            <RocketlaneFieldValue label={value.label} />
          </Button>
        );
      }

      default: {
        return (
          <Button variant="link" onClick={onTriggerClick} loading={loading}>
            {value.label}
          </Button>
        );
      }
    }
  }

  return (
    <CustomButton
      variant="link"
      onClick={onTriggerClick}
      loading={loading}
      data-cy={
        type === 'salesforce'
          ? 'integrations.salesforce.field_add_button'
          : 'integrations.rocketlane.field_add_button'
      }
    >
      {type === 'salesforce' ? (
        <Space size="small">
          <FontAwesomeIcon icon={faSalesforce} color="var(--icon-primary)" />
          {triggerLabel ?? <FormattedMessage id="add_field" />}
        </Space>
      ) : (
        (triggerLabel ?? <FormattedMessage id="add_field" />)
      )}
    </CustomButton>
  );
};

type OptionArray = {
  label: string;
  options: CustomOptionsType[];
};

const selectStyles = {
  menu: () => ({
    boxShadow: 'none',
    flex: 1,
    overflow: 'hidden',
    marginTop: 'var(--spacing-05) !important',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: initial,
    height: '100%',
  }),
  control: (provided) => ({ ...provided, margin: '0 var(--spacing-05)' }),
  group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
  groupHeading: (provided) => ({ ...provided, marginBottom: 0, flex: 1 }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'flex !important',
  }),
};

const CustomOption = (props) => {
  const { data, label } = props;

  const type = getUiFieldType(data.extra);

  return (
    <RSComponents.Option {...props}>
      <Space size={0}>
        <IconShell size="xsmall" icon={getFieldIcon(type)} />
        <Typography.Text ellipsis={{ tooltip: true }}>{label}</Typography.Text>
      </Space>
    </RSComponents.Option>
  );
};

export const PopoverSelect = ({
  type,
  value,
  options,
  loading,
  onChange,
  triggerLabel,
  onVisibilityChange,
  fieldObject,
  zIndex,
  parentVisible,
}: PopoverSelectProps) => {
  const [visible, setVisible] = useState(false);
  const [searchedText, setSearchedText] = useState<string>('');
  const intl = useIntl();

  // Reset visibility when parent modal closes
  useEffect(() => {
    if (parentVisible === false) {
      setVisible(false);
      setSearchedText('');
    }
  }, [parentVisible]);

  // Reset visibility when component unmounts to prevent popover from staying open
  useEffect(() => {
    return () => {
      setVisible(false);
    };
  }, []);

  const handleVisibilityToggle = (currentVisibility: boolean) => {
    setVisible(currentVisibility);
    onVisibilityChange?.(currentVisibility);
  };

  const onSelection = (props) => {
    if (onChange) {
      onChange(props);
    }
    handleVisibilityToggle(false);
  };

  const tempObj = groupBy(options, 'parent');

  let groupedOptions: OptionArray[] = Object.keys(tempObj).map((key) => {
    return {
      label:
        key === 'undefined'
          ? fieldObject?.externalResourceType ||
            intl.formatMessage({ id: 'fields' })
          : key,
      options: [
        ...tempObj[key].map((option) => {
          return {
            ...option,
            label:
              option.label.indexOf(']') > -1
                ? option.label.split(']')[1].trim()
                : option.label,
          };
        }),
      ],
    };
  });

  const nativeFields: OptionArray | undefined = groupedOptions.find(
    (option) => option.label === fieldObject.externalResourceType,
  );

  const rocketlaneFields: OptionArray | undefined = groupedOptions.find(
    (option) => option.label === 'Rocketlane',
  );

  if (nativeFields !== undefined) {
    groupedOptions = [
      nativeFields,
      ...groupedOptions.filter(
        (option) => option.label !== fieldObject.externalResourceType,
      ),
    ];
  } else if (rocketlaneFields !== undefined) {
    groupedOptions = [
      rocketlaneFields,
      ...groupedOptions.filter((option) => option.label !== 'Rocketlane'),
    ];
  }

  const SF_COMPONENTS = useMemo(
    () => ({
      ClearIndicator: null,
      IndicatorSeparator: null,
      DropdownIndicator,
      Control,
      Menu,
      MenuList: VirtualizedMenuList,
    }),
    [],
  );

  const RL_COMPONENTS = useMemo(
    () => ({
      ClearIndicator: null,
      IndicatorSeparator: null,
      DropdownIndicator,
      Control,
      Menu,
      MenuList: VirtualizedMenuList,
      Option: CustomOption,
    }),
    [],
  );

  return (
    <OutsideClickHandler
      allowed={isRelatedEl}
      onOutsideClick={() => handleVisibilityToggle(false)}
    >
      <Popover
        overlayClassName="popover"
        placement="bottomLeft"
        visible={visible}
        zIndex={zIndex}
        data-cy={
          type === 'salesforce'
            ? 'pop.salesforce_field_value_add'
            : 'pop.rocketlane_field_value_add'
        }
        content={
          <SelectContainer>
            <Select
              menuIsOpen
              autoFocus
              isLoading={loading}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              components={type === 'salesforce' ? SF_COMPONENTS : RL_COMPONENTS}
              formatOptionLabel={
                type === 'salesforce'
                  ? SalesforceFieldValue
                  : RocketlaneFieldValue
              }
              isClearable
              placeholder={<FormattedMessage id="placeholder.search" />}
              tabSelectsValue={false}
              styles={selectStyles}
              options={groupedOptions}
              grouped
              value={value}
              onChange={onSelection}
              onBlur={() => handleVisibilityToggle(false)}
              searchedText={searchedText}
              onInputChange={(input) => setSearchedText(input)}
            />
          </SelectContainer>
        }
      >
        <TriggerRenderer
          loading={loading}
          type={type}
          triggerLabel={triggerLabel}
          value={value}
          onTriggerClick={() => handleVisibilityToggle(true)}
        />
      </Popover>
    </OutsideClickHandler>
  );
};
